/* Local primitive catalogue for dedupr.cloud — small, deliberately not the
   product's packages/ui. See src/styles/tokens.css for why. */

.land-container {
  max-width: var(--land-content-width);
  margin: 0 auto;
  padding-left: var(--land-content-pad);
  padding-right: var(--land-content-pad);
}

/* ---- Button ---- */

.land-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: 0;
  border-radius: var(--land-radius-pill);
  font-family: var(--land-font-body);
  font-weight: 600;
  white-space: nowrap;
  transition: background-color 0.15s ease, opacity 0.15s ease, color 0.15s ease;
}

.land-btn--lg {
  height: 48px;
  padding: 0 22px;
  font-size: 15px;
}

.land-btn--md {
  height: 44px;
  padding: 0 20px;
  font-size: 14.5px;
}

.land-btn--sm {
  height: 36px;
  padding: 0 15px;
  font-size: 13.5px;
}

.land-btn--xs {
  height: 28px;
  padding: 0 11px;
  font-size: 12px;
}

.land-btn--primary {
  background: var(--land-accent);
  color: var(--land-on-accent);
}
.land-btn--primary:hover {
  background: var(--land-accent-hover);
}

.land-btn--secondary {
  background: transparent;
  color: var(--land-text);
  border: 1px solid var(--land-border-strong);
}
.land-btn--secondary:hover {
  background: var(--land-border-soft);
}

.land-btn--tint {
  background: var(--land-accent-tint-bg);
  color: var(--land-accent-tint-text);
  border: 1px solid var(--land-accent-tint-border);
}
.land-btn--tint:hover {
  background: var(--land-accent-tint-hover);
}

.land-btn--tint-outline {
  background: #fff;
  color: var(--land-accent-tint-text);
  border: 1px solid var(--land-accent-tint-border-strong);
}
.land-btn--tint-outline:hover {
  background: var(--land-accent-tint-hover);
}

.land-btn--dark {
  background: var(--land-text);
  color: var(--land-on-accent);
}
.land-btn--dark:hover {
  background: var(--land-accent);
}

.land-btn--inverse {
  background: var(--land-on-accent);
  color: var(--land-accent-hover);
}
.land-btn--inverse:hover {
  opacity: 0.9;
}

.land-btn--outline-on-dark {
  background: transparent;
  color: var(--land-on-accent);
  border: 1px solid #3f7a65;
}
.land-btn--outline-on-dark:hover {
  background: var(--land-accent-hover);
}

.land-btn--plain {
  background: transparent;
  color: inherit;
  padding: 0;
  height: auto;
  border-radius: 0;
  font-size: 14px;
}
.land-btn--plain:hover {
  color: var(--land-text);
}

.land-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* ---- Icon button (circular) ---- */

.land-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  border: 0;
  border-radius: 50%;
  background: var(--land-border-soft);
  color: var(--land-text-muted);
  font-family: var(--land-font-body);
  transition: background-color 0.15s ease;
}
.land-icon-btn:hover {
  background: var(--land-border-strong);
}

/* ---- Eyebrow / mono badge ---- */

.land-eyebrow {
  display: inline-block;
  font-family: var(--land-font-mono);
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--land-accent);
  border: 1px solid var(--land-accent-tint-border);
  background: var(--land-accent-tint-bg);
  border-radius: 13px;
  padding: 5px 11px;
}

.land-mono-label {
  font-family: var(--land-font-mono);
  font-size: 11px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--land-accent);
}

/* ---- Tag chip ---- */

.land-tag {
  padding: 5px 10px;
  border: 1px solid var(--land-border);
  border-radius: 12px;
  font-family: var(--land-font-mono);
  font-size: 11.5px;
  color: var(--land-text-body);
}

/* ---- Card ---- */

.land-card {
  background: var(--land-surface);
  border: 1px solid var(--land-border);
  border-radius: var(--land-radius-lg);
  padding: 24px;
}

.land-card--tint {
  background: var(--land-accent-tint-bg);
  border-color: var(--land-accent-tint-border);
}

.land-card--accent {
  background: var(--land-accent);
  border-color: var(--land-accent);
  color: var(--land-on-accent);
}

/* ---- Checklist ----
   Shared because three sections now make the same "here is what you get" list,
   and three copies of ten lines of CSS is how they end up looking different. */

.land-checklist {
  margin: 22px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  font-size: 14.5px;
}

.land-checklist li {
  display: flex;
  gap: 10px;
}

.land-checklist__mark {
  color: var(--land-accent);
  font-weight: 700;
  flex: 0 0 auto;
}

/* ---- Visually hidden, still announced ----
   For cells whose meaning is carried by a glyph. A bare ✓ in a comparison table
   is not an accessible value. */

.land-sr {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* ---- Shared section layout ---- */

.land-section {
  padding-top: clamp(44px, 6vw, 76px);
}

.land-section__head {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 20px;
  flex-wrap: wrap;
}

.land-section__title {
  margin: 0;
  font-size: clamp(26px, 3.4vw, 40px);
  font-weight: 600;
  letter-spacing: -1.4px;
  max-width: 620px;
  text-wrap: balance;
}

.land-section__lede {
  margin: 0;
  font-size: 15px;
  color: var(--land-text-muted);
}

.land-section__lede--narrow {
  max-width: 360px;
  text-wrap: pretty;
}

.land-section__lede--wide {
  max-width: 560px;
  text-wrap: pretty;
}
